'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { ThemeProvider as NextThemesProvider, type ThemeProviderProps as NextThemeProviderProps } from 'next-themes'
import { useThemeStore } from '@/lib/stores/theme-store'
import { getWhiteLabelDomain } from '@/lib/utils'

interface ThemeProviderProps {
  children: React.ReactNode
  attribute?: NextThemeProviderProps['attribute']
  defaultTheme?: string
  enableSystem?: boolean
  forcedTheme?: string
}

/**
 * Provider que combina next-themes (dark/light mode) com nosso sistema de white-label
 * 
 * Fluxo:
 * 1. theme-init.js já aplicou o tema do cache (se existir)
 * 2. Este provider inicializa o store React
 * 3. Se necessário, faz fetch ou revalidação em background
 * 4. Em cada navegação, verifica se cache está stale e revalida
 */
export function ThemeProvider({
  children,
  attribute = 'class',
  defaultTheme = 'dark',
  enableSystem = false,
  forcedTheme = 'dark'
}: ThemeProviderProps) {
  const { initialize, isInitialized, checkAndRevalidate } = useThemeStore()
  const pathname = usePathname()
  const isFirstRender = useRef(true)

  // Inicialização
  useEffect(() => {
    if (typeof window === 'undefined') return

    const domain = getWhiteLabelDomain(window.location.hostname)

    if (domain && !isInitialized) {
      initialize(domain)
    }
  }, [initialize, isInitialized])

  // Revalidação em background após navegação
  useEffect(() => {
    // Pular primeira renderização (já foi feita na inicialização)
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    if (typeof window === 'undefined' || !isInitialized) return

    const domain = getWhiteLabelDomain(window.location.hostname)

    if (domain) {
      console.log(`🧭 [Theme] Navigation detected: ${pathname}, checking revalidation...`)
      checkAndRevalidate(domain)
    }
  }, [pathname, isInitialized, checkAndRevalidate])

  return (
    <NextThemesProvider
      attribute={attribute}
      defaultTheme={defaultTheme}
      enableSystem={enableSystem}
      forcedTheme={forcedTheme}
    >
      {children}
    </NextThemesProvider>
  )
}
